import React, { useMemo, useRef, useState } from 'react';
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AutoStatusToast from '../components/common/AutoStatusToast';
import AdminLearningContentTable from '../components/admin/AdminLearningContentTable';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import { useProgramSidebar } from '../contexts/ProgramSidebarContext';
import { useAdminLearningContentRows } from '../hooks/useAdminLearningContentRows';
import { AdminLearningContentSubCategory, AdminLearningContentRowUpsert } from '../types/domain';
import {
  createAdminLearningContentTemplate,
  deleteAdminLearningContentTemplate,
  getAdminLearningContentTemplates,
  reorderAdminLearningContentTemplates,
  renameAdminLearningContentTemplate,
} from '../services/adminLearningContentService';
import ProgramToolbarUndoRedoButtons from '../components/program/ProgramToolbarUndoRedoButtons';
import { usePlatformContentEditorAccess } from '../hooks/usePlatformContentEditorAccess';

const GRADE_TABS = [1, 2, 3, 4, 5, 6] as const;
const SUB_CATEGORIES: Array<{ key: AdminLearningContentSubCategory; label: (grade: number) => string }> = [
  { key: 'term1', label: (grade) => `${grade}학년 1학기` },
  { key: 'term2', label: (grade) => `${grade}학년 2학기` },
  { key: 'common', label: (grade) => `${grade}학년 학기 구분 없음` },
];

type AddTemplateScope = { common: boolean; term1: boolean; term2: boolean };

function defaultAddTemplateScopeForSubCategory(
  sc: AdminLearningContentSubCategory
): AddTemplateScope {
  if (sc === 'common') return { common: true, term1: false, term2: false };
  if (sc === 'term1') return { common: false, term1: true, term2: false };
  return { common: false, term1: false, term2: true };
}

function addTemplateScopeToTargets(s: AddTemplateScope): AdminLearningContentSubCategory[] {
  const out: AdminLearningContentSubCategory[] = [];
  if (s.term1) out.push('term1');
  if (s.term2) out.push('term2');
  if (s.common) out.push('common');
  return out;
}

function pickPrimarySubCategory(
  targets: AdminLearningContentSubCategory[]
): AdminLearningContentSubCategory {
  const order: AdminLearningContentSubCategory[] = ['term1', 'term2', 'common'];
  for (const k of order) {
    if (targets.includes(k)) return k;
  }
  return targets[0];
}

const SortableTemplateItem: React.FC<{
  id: string;
  active: boolean;
  busy: boolean;
  onSelect: () => void;
  onRename: () => void;
  onDelete: () => void;
}> = ({ id, active, busy, onSelect, onRename, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.84 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`cp-admin-learning-template-item${active ? ' is-active' : ''}${isDragging ? ' is-dragging' : ''}`}
      aria-selected={active}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      {...attributes}
      {...listeners}
    >
      <div className="cp-admin-learning-template-tab">{id}</div>
      <div className="cp-admin-learning-template-item-actions">
        <button
          type="button"
          className="track-icon-button cp-admin-learning-template-icon-btn"
          title="편제 이름 수정"
          onClick={(e) => {
            e.stopPropagation();
            onRename();
          }}
          disabled={busy}
          aria-label="편제 수정"
        >
          <img src="/edit.svg" alt="편제 수정" className="track-icon" />
        </button>
        <button
          type="button"
          className="track-icon-button cp-admin-learning-template-icon-btn cp-admin-learning-template-icon-btn-delete"
          title="편제 삭제"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          disabled={busy}
          aria-label="편제 삭제"
        >
          <img src="/Trash.svg" alt="편제 삭제" className="track-icon" />
        </button>
      </div>
    </div>
  );
};

const AdminLearningContentPage: React.FC = () => {
  const { ready: accessReady, allowed: canAccess } = usePlatformContentEditorAccess();
  const { workspaceId } = useProgramSidebar();
  const [gradeNumber, setGradeNumber] = useState<(typeof GRADE_TABS)[number]>(1);
  const [subCategory, setSubCategory] = useState<AdminLearningContentSubCategory>('term1');
  const [templates, setTemplates] = useState<string[]>([]);
  const [templateName, setTemplateName] = useState('');
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [addTemplateScope, setAddTemplateScope] = useState<AddTemplateScope>(() =>
    defaultAddTemplateScopeForSubCategory('term1')
  );
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameSourceName, setRenameSourceName] = useState('');
  const [renameDraftName, setRenameDraftName] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetName, setDeleteTargetName] = useState('');
  const [templateBusy, setTemplateBusy] = useState(false);
  const createTemplateConfirmRef = useRef<HTMLButtonElement>(null);
  const templateNameRef = useRef(templateName);
  templateNameRef.current = templateName;
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const { toastState: saveToastState, showSuccess: showSaveSuccess } = useAutoStatusToast(3000);
  const [tableActions, setTableActions] = useState<{
    undo: () => void;
    redo: () => void;
    save: () => Promise<void>;
  } | null>(null);
  const [tableActionState, setTableActionState] = useState({
    canUndo: false,
    canRedo: false,
    canSave: false,
  });
  const { setAdminLearningDirty, saveAdminLearningRef } = React.useContext(GradeTrackLeaveContext);
  const [templateOrderDirty, setTemplateOrderDirty] = useState(false);
  /** key=currentName, value=originalName(마지막 DB 반영 기준) */
  const [templateOriginalNameByCurrent, setTemplateOriginalNameByCurrent] = useState<Record<string, string>>({});
  const { rows, loading, saving, error, saveRows } = useAdminLearningContentRows({
    workspaceId,
    gradeNumber,
    subCategory,
    templateName,
  });

  React.useEffect(() => {
    setTableActionState({ canUndo: false, canRedo: false, canSave: false });
  }, [gradeNumber, subCategory, templateName, loading, templatesLoading]);

  const subCategoryLabel = useMemo(
    () => SUB_CATEGORIES.find((v) => v.key === subCategory)?.label(gradeNumber) ?? '',
    [gradeNumber, subCategory]
  );
  const templateDndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    })
  );

  const handleSave = React.useCallback(
    async (nextRows: AdminLearningContentRowUpsert[]) => {
      await saveRows(nextRows);
      showSaveSuccess(`${subCategoryLabel} 데이터가 저장되었습니다.`);
    },
    [saveRows, showSaveSuccess, subCategoryLabel]
  );

  React.useEffect(() => {
    let cancelled = false;
    setTemplatesLoading(true);
    const preferredTemplateName = templateNameRef.current.trim();
    void getAdminLearningContentTemplates(workspaceId, gradeNumber, subCategory)
      .then((list) => {
        if (cancelled) return;
        const names = list
          .map((row) => (row.template_name ?? '').trim())
          .filter(Boolean);
        setTemplates(names);
        setTemplateName(
          preferredTemplateName && names.includes(preferredTemplateName)
            ? preferredTemplateName
            : names[0] ?? ''
        );
        setTemplateOrderDirty(false);
        setTemplateOriginalNameByCurrent(
          Object.fromEntries(names.map((name) => [name, name]))
        );
      })
      .catch(() => {
        if (cancelled) return;
        setTemplates([]);
        setTemplateName('');
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gradeNumber, workspaceId, subCategory]);

  const handleCreateTemplate = async () => {
    const name = newTemplateName.trim();
    const targets = addTemplateScopeToTargets(addTemplateScope);
    if (!name || targets.length === 0) return;
    setTemplateBusy(true);
    try {
      for (const sc of targets) {
        await createAdminLearningContentTemplate({
          workspaceId,
          gradeNumber,
          subCategory: sc,
          templateName: name,
        });
      }
      const primary = pickPrimarySubCategory(targets);
      const list = await getAdminLearningContentTemplates(workspaceId, gradeNumber, primary);
      const names = list.map((row) => (row.template_name ?? '').trim()).filter(Boolean);
      setSubCategory(primary);
      setTemplates(names);
      setTemplateName(name);
      setTemplateOrderDirty(false);
      setTemplateOriginalNameByCurrent(Object.fromEntries(names.map((template) => [template, template])));
      setTemplateModalOpen(false);
      setNewTemplateName('');
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleTemplateDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const fromName = String(active.id ?? '');
    const toName = String(over.id ?? '');
    if (!fromName || !toName || fromName === toName) return;
    const from = templates.indexOf(fromName);
    const to = templates.indexOf(toName);
    if (from < 0 || to < 0) return;
    const next = arrayMove(templates, from, to);
    setTemplates(next);
    setTemplateOrderDirty(true);
  };

  const openRenameModal = (name: string) => {
    setRenameSourceName(name);
    setRenameDraftName(name);
    setRenameModalOpen(true);
  };

  const openDeleteModal = (name: string) => {
    setDeleteTargetName(name);
    setDeleteModalOpen(true);
  };

  const handleRenameTemplate = async () => {
    const nextName = renameDraftName.trim();
    const fromName = renameSourceName.trim();
    if (!fromName || !nextName || nextName === fromName) {
      setRenameModalOpen(false);
      return;
    }
    const nextTemplates = templates.map((name) => (name === fromName ? nextName : name));
    setTemplates(nextTemplates);
    setTemplateOriginalNameByCurrent((prev) => {
      const next = { ...prev };
      const originalName = next[fromName] ?? fromName;
      delete next[fromName];
      next[nextName] = originalName;
      return next;
    });
    if (templateName === fromName) setTemplateName(nextName);
    setRenameModalOpen(false);
  };

  const handleDeleteTemplate = async () => {
    const name = deleteTargetName.trim();
    if (!name) return;
    setTemplateBusy(true);
    try {
      await deleteAdminLearningContentTemplate({
        workspaceId,
        gradeNumber,
        subCategory,
        templateName: name,
      });
      const next = templates.filter((v) => v !== name);
      setTemplates(next);
      setTemplateOriginalNameByCurrent((prev) => {
        const out = { ...prev };
        delete out[name];
        return out;
      });
      if (templateName === name) {
        setTemplateName(next[0] ?? '');
      }
      setDeleteModalOpen(false);
      setDeleteTargetName('');
      setTemplateOrderDirty(false);
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleSaveAll = async () => {
    const pendingRenames = Object.entries(templateOriginalNameByCurrent)
      .filter(([currentName, originalName]) => !!currentName && !!originalName && currentName !== originalName)
      .map(([currentName, originalName]) => ({ fromName: originalName, toName: currentName }));
    if (!tableActionState.canSave && !templateOrderDirty && pendingRenames.length === 0) return;
    setTemplateBusy(true);
    try {
      for (const rename of pendingRenames) {
        await renameAdminLearningContentTemplate({
          workspaceId,
          gradeNumber,
          subCategory,
          fromTemplateName: rename.fromName,
          toTemplateName: rename.toName,
        });
      }
      if (templateOrderDirty) {
        await reorderAdminLearningContentTemplates({
          workspaceId,
          gradeNumber,
          subCategory,
          orderedTemplateNames: templates,
        });
        setTemplateOrderDirty(false);
      }
      if (tableActionState.canSave) {
        await tableActions?.save();
      }
      setTemplateOriginalNameByCurrent(
        Object.fromEntries(templates.map((name) => [name, name]))
      );
      showSaveSuccess(`${subCategoryLabel} 데이터가 저장되었습니다.`);
    } finally {
      setTemplateBusy(false);
    }
  };

  const hasPendingRename = Object.entries(templateOriginalNameByCurrent).some(
    ([currentName, originalName]) => currentName !== originalName
  );
  const rowsReady = Boolean(templateName.trim()) && !templatesLoading && !loading;
  const isPageDirty =
    rowsReady &&
    (tableActionState.canSave || templateOrderDirty || hasPendingRename);

  React.useEffect(() => {
    setAdminLearningDirty(isPageDirty);
    return () => setAdminLearningDirty(false);
  }, [isPageDirty, setAdminLearningDirty]);

  React.useEffect(() => {
    saveAdminLearningRef.current = { save: handleSaveAll };
    return () => {
      saveAdminLearningRef.current = null;
    };
  }, [handleSaveAll, saveAdminLearningRef]);

  if (!accessReady) {
    return (
      <section className="cp-admin-learning-page">
        <h2>학습내용 관리</h2>
        <p className="cp-dashboard-placeholder" role="status">
          확인 중…
        </p>
      </section>
    );
  }

  if (!canAccess) {
    return (
      <section className="cp-admin-learning-page">
        <h2>학습내용 관리</h2>
        <p className="cp-dashboard-placeholder" role="alert">
          이 메뉴는 관리자 또는 매니저만 사용할 수 있습니다.
        </p>
      </section>
    );
  }

  return (
    <section className="cp-admin-learning-page">
      <div className="cp-page-title-row cp-admin-notices-page-title-row cp-evaluation-plan-page-title-row">
        <div className="cp-admin-legal-title-stack">
          <h2>학습내용 관리</h2>
          <p className="cp-admin-legal-subtitle">
            플랫폼 전역 학습내용입니다. 모든 로그인 사용자가 조회할 수 있으며, 편집은 플랫폼 관리자·매니저만
            가능합니다.
          </p>
        </div>
        <div className="cp-annual-top-actions-save cp-admin-learning-top-right-actions cp-page-save-actions--chunky" role="group" aria-label="편집 저장 도구">
          <ProgramToolbarUndoRedoButtons
            canUndo={tableActionState.canUndo}
            canRedo={tableActionState.canRedo}
            onUndo={() => tableActions?.undo()}
            onRedo={() => tableActions?.redo()}
            disabled={!tableActions || !rowsReady}
          />
          <button
            type="button"
            className="cp-btn-save-all cp-admin-learning-top-save-btn"
            onClick={() => void handleSaveAll()}
            disabled={templateBusy || saving || !isPageDirty}
            title={isPageDirty ? '저장하기' : '저장할 변경사항 없음'}
          >
            {templateBusy || saving ? '저장 중…' : isPageDirty ? '저장하기' : '변경사항 없음'}
          </button>
        </div>
      </div>

      <div className="cp-admin-learning-top-tabs-row">
        <div className="cp-admin-learning-grade-tabs" role="tablist" aria-label="학년 카테고리">
          {GRADE_TABS.map((grade) => (
            <button
              key={grade}
              type="button"
              role="tab"
              aria-selected={gradeNumber === grade}
              className={`cp-admin-learning-grade-tab${gradeNumber === grade ? ' is-active' : ''}`}
              onClick={() => setGradeNumber(grade)}
            >
              {grade}
            </button>
          ))}
        </div>
        <div className="cp-admin-learning-sub-tabs" role="tablist" aria-label="하위 카테고리">
          {SUB_CATEGORIES.map((category) => (
            <button
              key={category.key}
              type="button"
              role="tab"
              aria-selected={subCategory === category.key}
              className={`cp-admin-learning-sub-tab cp-admin-learning-sub-tab--${category.key}${
                subCategory === category.key ? ' is-active' : ''
              }`}
              onClick={() => setSubCategory(category.key)}
            >
              {category.label(gradeNumber)}
            </button>
          ))}
        </div>
      </div>

      <div className="cp-admin-learning-template-tabs-row">
        <button
          type="button"
          className="cp-btn-copy-paste cp-admin-learning-add-template-btn"
          aria-label="편제 추가하기"
          onClick={() => {
            setAddTemplateScope(defaultAddTemplateScopeForSubCategory(subCategory));
            setNewTemplateName('');
            setTemplateModalOpen(true);
          }}
        >
          <span className="cp-admin-learning-add-template-label cp-admin-learning-add-template-label--full">편제 추가하기 +</span>
          <span className="cp-admin-learning-add-template-label cp-admin-learning-add-template-label--short">편제 +</span>
        </button>
        <div className="cp-admin-learning-template-tabs" role="tablist" aria-label="편제 목록">
          <DndContext sensors={templateDndSensors} collisionDetection={closestCenter} onDragEnd={handleTemplateDragEnd}>
            <SortableContext items={templates} strategy={horizontalListSortingStrategy}>
              {templates.map((name) => (
                <SortableTemplateItem
                  key={name}
                  id={name}
                  active={templateName === name}
                  busy={templateBusy}
                  onSelect={() => setTemplateName(name)}
                  onRename={() => openRenameModal(name)}
                  onDelete={() => openDeleteModal(name)}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>

      {error ? <p className="cp-admin-learning-error">{error}</p> : null}

      {templatesLoading ? (
        <p className="cp-admin-learning-empty" role="status">
          편제 목록을 불러오는 중…
        </p>
      ) : !templateName ? (
        <p className="cp-admin-learning-empty">편제를 먼저 추가해 주세요.</p>
      ) : loading ? (
        <p className="cp-admin-learning-empty" role="status">
          학습내용을 불러오는 중…
        </p>
      ) : (
        <AdminLearningContentTable
          key={`${gradeNumber}-${subCategory}-${templateName}`}
          workspaceId={null}
          gradeNumber={gradeNumber}
          subCategory={subCategory}
          templateName={templateName}
          toolbarTitle={`${subCategoryLabel} - ${templateName}`}
          rows={rows}
          loading={false}
          saving={saving}
          onSave={handleSave}
          showInlineActionButtons={false}
          onBindActions={setTableActions}
          onActionStateChange={setTableActionState}
          omitEmptyRowsOnSave={false}
        />
      )}

      {templateModalOpen ? (
        <div className="cp-modal-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && setTemplateModalOpen(false)}>
          <div className="cp-modal cp-admin-learning-template-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <h3>편제 이름 입력</h3>
            <div className="cp-modal-body">
              <div
                className="cp-admin-learning-template-modal-scopes"
                role="group"
                aria-label="편제를 추가할 위치"
              >
                <button
                  type="button"
                  className={`cp-program-toolbar-undo-redo-btn cp-admin-learning-template-scope-pill${
                    addTemplateScope.term1 ? ' is-on' : ''
                  }`}
                  onClick={() => setAddTemplateScope((prev) => ({ common: false, term1: !prev.term1, term2: prev.term2 }))}
                >
                  1학기
                </button>
                <button
                  type="button"
                  className={`cp-program-toolbar-undo-redo-btn cp-admin-learning-template-scope-pill${
                    addTemplateScope.term2 ? ' is-on' : ''
                  }`}
                  onClick={() => setAddTemplateScope((prev) => ({ common: false, term1: prev.term1, term2: !prev.term2 }))}
                >
                  2학기
                </button>
                <button
                  type="button"
                  className={`cp-program-toolbar-undo-redo-btn cp-admin-learning-template-scope-pill${
                    addTemplateScope.common ? ' is-on' : ''
                  }`}
                  onClick={() => setAddTemplateScope({ common: true, term1: false, term2: false })}
                >
                  학기 구분 없음
                </button>
              </div>
              <input
                type="text"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                onKeyDown={(e) => {
                  const native = e.nativeEvent as KeyboardEvent;
                  if (native.isComposing || e.key !== 'Enter') return;
                  e.preventDefault();
                  createTemplateConfirmRef.current?.focus({ preventScroll: true });
                }}
                placeholder="편제 이름"
                autoFocus
                className="cp-admin-learning-template-modal-name-input"
              />
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setTemplateModalOpen(false)}>
                취소
              </button>
              <button
                ref={createTemplateConfirmRef}
                type="button"
                className="cp-btn-primary"
                onClick={() => void handleCreateTemplate()}
                disabled={
                  !newTemplateName.trim() ||
                  (!addTemplateScope.common && !addTemplateScope.term1 && !addTemplateScope.term2) ||
                  templateBusy
                }
              >
                확인
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {renameModalOpen ? (
        <div className="cp-modal-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && setRenameModalOpen(false)}>
          <div className="cp-modal cp-admin-learning-template-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <h3>편제 이름 수정</h3>
            <div className="cp-modal-body">
              <input
                type="text"
                value={renameDraftName}
                onChange={(e) => setRenameDraftName(e.target.value)}
                placeholder="새 편제 이름"
                autoFocus
              />
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setRenameModalOpen(false)}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => void handleRenameTemplate()}
                disabled={!renameDraftName.trim() || templateBusy}
              >
                수정
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {deleteModalOpen ? (
        <div className="cp-modal-backdrop" role="presentation" onMouseDown={(e) => e.target === e.currentTarget && setDeleteModalOpen(false)}>
          <div className="cp-modal cp-admin-learning-template-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <h3>편제 삭제</h3>
            <div className="cp-modal-body">
              <p>
                `{deleteTargetName}` 편제를 삭제하면 해당 편제의 학습내용 데이터도 모두 삭제됩니다.
                <br />
                계속하시겠습니까?
              </p>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setDeleteModalOpen(false)}>
                취소
              </button>
              <button
                type="button"
                className="cp-btn-primary"
                onClick={() => void handleDeleteTemplate()}
                disabled={templateBusy}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <button
        type="button"
        className="cp-fab-save"
        onClick={() => void handleSaveAll()}
        disabled={templateBusy || saving || !isPageDirty}
        aria-label="저장하기"
        title={isPageDirty ? '저장하기' : '저장할 변경사항 없음'}
      >
        <img
          src={`${process.env.PUBLIC_URL || ''}/Save.svg`}
          alt=""
          className="cp-fab-save-icon"
        />
      </button>
      <AutoStatusToast state={saveToastState} />
    </section>
  );
};

export default AdminLearningContentPage;
